import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UserRound, MapPin, CreditCard, Save, Plus, Trash2, Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const STATUS_STYLES = {
  completed: "bg-teal-100 text-teal-800 border-teal-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  matched: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
  cancelled_by_cleaner: "bg-red-100 text-red-700 border-red-200",
};

const emptyAddress = () => ({
  label: "Home", street_address: "", unit: "", city: "",
  state: "", zip: "", country: "US", is_default: false,
});

export default function ClientProfile() {
  const { user, refresh } = useApp();
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState(emptyAddress());
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [me, j] = await Promise.all([
        api.get("/auth/me"),
        api.get("/jobs/mine"),
      ]);
      setForm({ full_name: me.data.full_name || "", phone: me.data.phone || "" });
      setAddresses(me.data.saved_addresses || []);
      setJobs(j.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    if (!form.full_name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.put("/users/me", { full_name: form.full_name.trim(), phone: form.phone.trim() });
      toast.success("Profile updated");
      if (refresh) refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveAddresses = async (next) => {
    try {
      await api.put("/users/me", { saved_addresses: next });
      setAddresses(next);
      toast.success("Addresses saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const addAddress = async () => {
    if (!newAddr.street_address.trim() || !newAddr.city.trim() || !newAddr.zip.trim()) {
      return toast.error("Street, city, and ZIP are required");
    }
    let next = [...addresses, { ...newAddr, id: crypto.randomUUID?.() || String(Date.now()) }];
    if (newAddr.is_default || addresses.length === 0) {
      next = next.map(a => ({ ...a, is_default: a.id === next[next.length - 1].id }));
    }
    await saveAddresses(next);
    setNewAddr(emptyAddress());
  };

  const removeAddress = async (id) => {
    const next = addresses.filter(a => a.id !== id);
    await saveAddresses(next);
  };

  const makeDefault = async (id) => {
    const next = addresses.map(a => ({ ...a, is_default: a.id === id }));
    await saveAddresses(next);
  };

  const paymentHistory = useMemo(() => {
    return (jobs || []).filter(j => j.payment_status === "paid").slice(0, 50);
  }, [jobs]);

  const totalSpent = useMemo(() => paymentHistory.reduce((s, j) => s + (j.total || 0), 0), [paymentHistory]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="client-profile">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Client · Profile</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Your profile</h1>
        <p className="text-slate-600 mt-2">Edit your details, manage saved addresses, and review your payment history.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><UserRound className="w-5 h-5 text-teal-600" />Account details</h2>
            <div><Label>Full name *</Label><Input data-testid="prof-name" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1 bg-slate-50" /></div>
            <div><Label>Phone</Label><Input data-testid="prof-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="+1 555 000 0000" /></div>
            <Button onClick={saveProfile} disabled={saving} data-testid="prof-save" className="bg-teal-600 hover:bg-teal-700 text-white">
              <Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save changes"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-teal-600" />Saved addresses</h2>
            <div className="space-y-2" data-testid="prof-addresses">
              {addresses.length === 0 ? (
                <div className="text-sm text-slate-500 py-3">No saved addresses yet.</div>
              ) : addresses.map(a => (
                <div key={a.id} className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`prof-addr-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{a.label}</span>
                        {a.is_default && <Badge className="text-[10px] bg-teal-100 text-teal-800 border-teal-200"><Star className="w-3 h-3 mr-0.5" />Default</Badge>}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        {a.street_address}{a.unit ? `, ${a.unit}` : ""}<br/>
                        {a.city}, {a.state} {a.zip} · {a.country}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!a.is_default && <button onClick={() => makeDefault(a.id)} data-testid={`prof-addr-default-${a.id}`} className="text-[11px] text-teal-600 hover:underline">Make default</button>}
                      <button onClick={() => removeAddress(a.id)} data-testid={`prof-addr-remove-${a.id}`} className="text-[11px] text-red-500 hover:underline flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Add address</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Input placeholder="Label (Home, Office…)" value={newAddr.label} onChange={e => setNewAddr({ ...newAddr, label: e.target.value })} data-testid="newaddr-label" /></div>
                <div className="col-span-2"><Input placeholder="Street address *" value={newAddr.street_address} onChange={e => setNewAddr({ ...newAddr, street_address: e.target.value })} data-testid="newaddr-street" /></div>
                <Input placeholder="Apt / Unit" value={newAddr.unit} onChange={e => setNewAddr({ ...newAddr, unit: e.target.value })} data-testid="newaddr-unit" />
                <Input placeholder="City *" value={newAddr.city} onChange={e => setNewAddr({ ...newAddr, city: e.target.value })} data-testid="newaddr-city" />
                <Input placeholder="State" value={newAddr.state} onChange={e => setNewAddr({ ...newAddr, state: e.target.value })} data-testid="newaddr-state" />
                <Input placeholder="ZIP *" value={newAddr.zip} onChange={e => setNewAddr({ ...newAddr, zip: e.target.value })} data-testid="newaddr-zip" />
                <Input placeholder="Country" value={newAddr.country} onChange={e => setNewAddr({ ...newAddr, country: e.target.value.toUpperCase() })} maxLength={2} data-testid="newaddr-country" />
                <label className="flex items-center gap-2 text-xs text-slate-600 col-span-2">
                  <input type="checkbox" checked={newAddr.is_default} onChange={e => setNewAddr({ ...newAddr, is_default: e.target.checked })} data-testid="newaddr-default" className="accent-teal-600" />
                  Set as default
                </label>
              </div>
              <Button onClick={addAddress} data-testid="newaddr-save" className="mt-2 bg-slate-900 text-white">
                <Plus className="w-4 h-4 mr-1" />Add address
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><CreditCard className="w-5 h-5 text-teal-600" />Payment history</h2>
            <div className="text-xs text-slate-500">Total paid: <b className="text-slate-800">${totalSpent.toFixed(2)}</b></div>
          </div>
          {paymentHistory.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">No payments yet — book your first cleaning to see charges here.</div>
          ) : (
            <div className="space-y-2" data-testid="prof-payments">
              {paymentHistory.map(j => (
                <div key={j.id} className="p-3 rounded-lg border border-slate-200 bg-white flex items-center justify-between gap-3" data-testid={`prof-pay-${j.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      Booking {j.id?.slice(0, 8) || ""} · {j.bedrooms || 0} bed / {j.bathrooms || 0} bath
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {j.scheduled_at ? new Date(j.scheduled_at).toLocaleDateString() : ""}
                      {j.address ? ` · ${j.address}` : (j.city ? ` · ${j.city}` : "")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-slate-900">${(j.total || 0).toFixed(2)}</div>
                    <Badge className={`text-[10px] mt-1 ${STATUS_STYLES[j.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{j.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
